import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Typography
} from '@mui/material';
import { GlobeIcon } from '../utils/icons';
import '../styles/components/_language-selector.scss';

interface LanguageSelectorProps {
  isMobile?: boolean;
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  isMobile = false,
  className = ''
}) => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    handleClose();
  };

  const currentLanguage = i18n.language.split('-')[0] || 'fr';

  // Determine icon color based on context
  const getIconColor = () => {
    if (className.includes('actions-sidebar__button') && !isMobile) {
      return '#13322B'; // Dark color for white background
    }
    return 'currentColor'; // Inherit from parent for dark backgrounds
  };

  return (
    <>
      <Tooltip title={t('common.language')} placement={isMobile ? "bottom" : "left"}>
        <IconButton 
          className={className}
          onClick={handleClick}
          aria-label={t('common.language')}
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            opacity: 1,
            color: className.includes('actions-sidebar__button') && !isMobile ? '#13322B' : 'inherit',
            '& svg': {
              opacity: 1,
              color: className.includes('actions-sidebar__button') && !isMobile ? '#13322B' : 'inherit',
              stroke: className.includes('actions-sidebar__button') && !isMobile ? '#13322B' : 'currentColor',
              strokeWidth: className.includes('actions-sidebar__button') && !isMobile ? 2 : 1.5,
            }
          }}
        >
          <GlobeIcon size={24} color={getIconColor()} />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff !important',
            opacity: '1 !important',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1) !important',
          }
        }}
        anchorOrigin={{
          vertical: isMobile ? 'top' : 'bottom',
          horizontal: isMobile ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'left' : 'right',
        }}
      >
        <MenuItem 
          onClick={() => handleLanguageChange('fr')}
          selected={currentLanguage === 'fr'}
          sx={{
            backgroundColor: '#ffffff !important',
            opacity: '1 !important',
            color: '#13322B !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1) !important',
              opacity: '1 !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--brand-primary-25) !important',
              opacity: '1 !important',
              fontWeight: '600 !important',
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'var(--brand-primary-35) !important',
              opacity: '1 !important',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">🇫🇷</Typography>
            <Typography variant="body2">{t('languageSelector.french')}</Typography>
            {currentLanguage === 'fr' && (
              <Typography variant="body2" sx={{ ml: 'auto', color: 'primary.main' }}>✓</Typography>
            )}
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleLanguageChange('en')}
          selected={currentLanguage === 'en'}
          sx={{
            backgroundColor: '#ffffff !important',
            opacity: '1 !important',
            color: '#13322B !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1) !important',
              opacity: '1 !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--brand-primary-25) !important',
              opacity: '1 !important',
              fontWeight: '600 !important',
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'var(--brand-primary-35) !important',
              opacity: '1 !important',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">🇬🇧</Typography>
            <Typography variant="body2">{t('languageSelector.english')}</Typography>
            {currentLanguage === 'en' && (
              <Typography variant="body2" sx={{ ml: 'auto', color: 'primary.main' }}>✓</Typography>
            )}
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleLanguageChange('pt')}
          selected={currentLanguage === 'pt'}
          sx={{
            backgroundColor: '#ffffff !important',
            opacity: '1 !important',
            color: '#13322B !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1) !important',
              opacity: '1 !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--brand-primary-25) !important',
              opacity: '1 !important',
              fontWeight: '600 !important',
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'var(--brand-primary-35) !important',
              opacity: '1 !important',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">🇵🇹</Typography>
            <Typography variant="body2">{t('languageSelector.portuguese')}</Typography>
            {currentLanguage === 'pt' && (
              <Typography variant="body2" sx={{ ml: 'auto', color: 'primary.main' }}>✓</Typography>
            )}
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};

export default LanguageSelector;

